import { useState, useEffect } from 'react';
import { Card, Button, Text } from '@shopify/polaris';

interface DebugInfo {
  timestamp: string;
  shopDetection: {
    urlParams: string;
    shopFromUrl: string | null;
    shopFromCache: string | null;
    shopFromLocalStorage: string | null;
    finalShop: string;
  };
  apiCalls: {
    settingsUrl: string;
    testConnectionUrl: string;
    lastResponse: any;
    lastError: any;
  };
  environment: {
    currentUrl: string;
    isEmbedded: boolean;
    hasShopifyGlobal: boolean;
  };
}

export function DebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [realTimeLog, setRealTimeLog] = useState<string[]>([]);

  const collectDebugInfo = (): DebugInfo => {
    // Collect URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const shopFromUrl = urlParams.get('shop');
    
    // Check cache and localStorage
    const shopFromCache = (window as any).globalShopCache || null;
    const shopFromLocalStorage = localStorage.getItem('shopify_shop_domain');
    
    // Determine final shop
    let finalShop = 'unknown';
    if (shopFromCache) {
      finalShop = shopFromCache;
    } else if (shopFromUrl) {
      finalShop = shopFromUrl;
    } else if (shopFromLocalStorage) {
      finalShop = shopFromLocalStorage;
    }

    return {
      timestamp: new Date().toISOString(),
      shopDetection: {
        urlParams: window.location.search,
        shopFromUrl,
        shopFromCache,
        shopFromLocalStorage,
        finalShop
      },
      apiCalls: {
        settingsUrl: `/api/katana/settings?shop=${encodeURIComponent(finalShop)}`,
        testConnectionUrl: `/api/katana/test-connection?shop=${encodeURIComponent(finalShop)}`,
        lastResponse: (window as any).__lastApiResponse || null,
        lastError: (window as any).__lastApiError || null
      },
      environment: {
        currentUrl: window.location.href,
        isEmbedded: window.top !== window,
        hasShopifyGlobal: !!(window as any).Shopify
      }
    };
  };

  const testApiCalls = async () => {
    const log: string[] = [];
    
    try {
      log.push(`[${new Date().toISOString()}] Starting API tests...`);
      
      const debugInfo = collectDebugInfo();
      const shop = debugInfo.shopDetection.finalShop;
      
      log.push(`[${new Date().toISOString()}] Using shop: ${shop}`);
      
      // Test settings endpoint
      log.push(`[${new Date().toISOString()}] Testing settings endpoint...`);
      const settingsUrl = `/api/katana/settings?shop=${encodeURIComponent(shop)}`;
      log.push(`[${new Date().toISOString()}] Settings URL: ${settingsUrl}`);
      
      const settingsResponse = await fetch(settingsUrl);
      log.push(`[${new Date().toISOString()}] Settings response status: ${settingsResponse.status}`);
      
      const settingsData = await settingsResponse.json();
      log.push(`[${new Date().toISOString()}] Settings response: ${JSON.stringify(settingsData, null, 2)}`);
      
      // Store response globally for debugging
      (window as any).__lastApiResponse = settingsData;
      
      // Test connection endpoint
      log.push(`[${new Date().toISOString()}] Testing connection endpoint...`);
      const connectionUrl = `/api/katana/test-connection?shop=${encodeURIComponent(shop)}`;
      log.push(`[${new Date().toISOString()}] Connection URL: ${connectionUrl}`);
      
      const connectionResponse = await fetch(connectionUrl);
      log.push(`[${new Date().toISOString()}] Connection response status: ${connectionResponse.status}`);
      
      const connectionData = await connectionResponse.json();
      log.push(`[${new Date().toISOString()}] Connection response: ${JSON.stringify(connectionData, null, 2)}`);
      
      log.push(`[${new Date().toISOString()}] API tests completed successfully`);
      
    } catch (error) {
      log.push(`[${new Date().toISOString()}] API test error: ${error}`);
      (window as any).__lastApiError = error;
    }
    
    setRealTimeLog(log);
  };

  useEffect(() => {
    if (isOpen) {
      setDebugInfo(collectDebugInfo());
    }
  }, [isOpen]);

  return (
    <div style={{ marginBottom: '1rem', position: 'relative' }}>
      <Card>
        <div style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <Text variant="headingMd" as="h3">
              🔧 Debug Panel
            </Text>
            <Button onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? 'Hide' : 'Show'} Debug Info
            </Button>
          </div>

          {isOpen && (
            <div style={{ 
              borderTop: '1px solid #E1E3E5', 
              paddingTop: '1rem',
              position: 'relative',
              zIndex: 1
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <Button onClick={() => setDebugInfo(collectDebugInfo())} variant="secondary">
                  Refresh Debug Info
                </Button>
                <span style={{ margin: '0 0.5rem' }} />
                <Button onClick={testApiCalls} variant="primary">
                  Test API Calls
                </Button>
                <span style={{ margin: '0 0.5rem' }} />
                <Button onClick={() => setRealTimeLog([])}>
                  Clear Log
                </Button>
              </div>

              {debugInfo && (
                <div style={{ display: 'grid', gap: '0.75rem', maxHeight: '70vh', overflow: 'auto' }}>
                  <div style={{ background: '#F6F6F7', padding: '0.75rem', borderRadius: '4px' }}>
                    <Text variant="headingSm" as="h4">Shop Detection</Text>
                    <div style={{ marginTop: '0.5rem', fontSize: '13px' }}>
                      <div>
                        <strong>Final Shop:</strong> 
                        <span style={{ 
                          marginLeft: '0.5rem', 
                          padding: '2px 6px', 
                          borderRadius: '3px',
                          background: debugInfo.shopDetection.finalShop !== 'unknown' ? '#D4EDDA' : '#F8D7DA',
                          color: debugInfo.shopDetection.finalShop !== 'unknown' ? '#155724' : '#721C24',
                          fontSize: '12px'
                        }}>
                          {debugInfo.shopDetection.finalShop}
                        </span>
                      </div>
                      <div><strong>URL Params:</strong> {debugInfo.shopDetection.urlParams}</div>
                      <div><strong>Shop from URL:</strong> {debugInfo.shopDetection.shopFromUrl || 'null'}</div>
                      <div><strong>Shop from Cache:</strong> {debugInfo.shopDetection.shopFromCache || 'null'}</div>
                      <div><strong>Shop from LocalStorage:</strong> {debugInfo.shopDetection.shopFromLocalStorage || 'null'}</div>
                    </div>
                  </div>

                  <div style={{ background: '#F6F6F7', padding: '1rem', borderRadius: '4px' }}>
                    <Text variant="headingSm" as="h4">API Endpoints</Text>
                    <div style={{ marginTop: '0.5rem', fontSize: '14px', wordBreak: 'break-all' }}>
                      <div><strong>Settings URL:</strong> {debugInfo.apiCalls.settingsUrl}</div>
                      <div><strong>Test Connection URL:</strong> {debugInfo.apiCalls.testConnectionUrl}</div>
                    </div>
                  </div>

                  <div style={{ background: '#F6F6F7', padding: '1rem', borderRadius: '4px' }}>
                    <Text variant="headingSm" as="h4">Environment</Text>
                    <div style={{ marginTop: '0.5rem', fontSize: '14px' }}>
                      <div><strong>Embedded:</strong> {debugInfo.environment.isEmbedded ? 'Yes' : 'No'}</div>
                      <div><strong>Shopify Global:</strong> {debugInfo.environment.hasShopifyGlobal ? 'Available' : 'Not Available'}</div>
                      <div style={{ wordBreak: 'break-all' }}><strong>Current URL:</strong> {debugInfo.environment.currentUrl}</div>
                    </div>
                  </div>

                  {debugInfo.apiCalls.lastResponse && (
                    <div style={{ background: '#F6F6F7', padding: '1rem', borderRadius: '4px' }}>
                      <Text variant="headingSm" as="h4">Last API Response</Text>
                      <pre style={{ 
                        background: '#fff', 
                        padding: '1rem', 
                        borderRadius: '4px', 
                        fontSize: '12px', 
                        overflow: 'auto',
                        marginTop: '0.5rem',
                        border: '1px solid #E1E3E5'
                      }}>
                        {JSON.stringify(debugInfo.apiCalls.lastResponse, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {realTimeLog.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <Text variant="headingSm" as="h4">Real-time API Test Log</Text>
                  <div style={{ 
                    background: '#000', 
                    color: '#00ff00', 
                    padding: '1rem', 
                    borderRadius: '4px', 
                    fontSize: '12px', 
                    fontFamily: 'monospace',
                    maxHeight: '300px',
                    overflow: 'auto',
                    marginTop: '0.5rem',
                    position: 'relative'
                  }}>
                    {realTimeLog.map((line, index) => (
                      <div key={index}>{line}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
} 